import { OsiActsWidgetViewModel } from '@widgets/osiActs/model/viewModel';
import { observer } from 'mobx-react-lite';
import { Box, Button, Card, CardHeader, Chip, Typography } from '@mui/material';
import { useTranslation } from '@shared/utils/i18n';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import { useState } from 'react';
import { SignedActsDialog } from '@widgets/osiActs/ui/signed/SignedActsDialog';
import { formatActAmount } from '@widgets/osiActs/ui/combined/format';

interface Props {
  viewModel: OsiActsWidgetViewModel;
}

export const SignedActs: React.FC<Props> = observer(({ viewModel }) => {
  const acts = viewModel.signedActs;
  const [pageSize, setPageSize] = useState(5);
  const { getGridLocale, getGridPaginationLocaleProps, t } = useTranslation();

  const columns: GridColDef[] = [
    {
      field: 'action',
      headerName: '',
      renderCell: (params) => (
        <Button
          onClick={() => {
            viewModel.onShowSignedActClick(params.row.id);
          }}
        >
          {t('common:view')}
        </Button>
      )
    },
    {
      field: 'actNum',
      headerName: t('acts:signedActs.actNumber'),
      minWidth: 260,
      // OSI-818: акт, подписанный в составе общего акта по БИН/ИИН, помечаем — его
      // «Просмотр»/«Сохранить» отдают общий документ по всем домам группы.
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <span>{params.value}</span>
          {params.row.combinedActId && (
            <Chip size="small" color="info" variant="outlined" label={t('acts:combinedAct.chip')} />
          )}
        </Box>
      )
    },
    {
      field: 'actPeriod',
      headerName: t('acts:signedActs.period'),
      minWidth: 200,
      valueFormatter: (params) => {
        const dt = new Date(params.value);
        return dt.toLocaleString('ru-RU', { year: 'numeric', month: 'long' });
      },
      // OSI-808: actPeriod приходит строкой ISO-даты, дефолтный компаратор сравнивал бы
      // её как текст — явно сравниваем по времени, чтобы сортировка была по датам
      sortComparator: (a, b) => new Date(a as string).getTime() - new Date(b as string).getTime()
    },
    { field: 'stateName', headerName: t('acts:signedActs.state'), minWidth: 200 },
    {
      field: 'totalItemsAmount',
      headerName: t('acts:signedActs.amount'),
      minWidth: 170,
      align: 'right',
      // OSI-818: сумма строк может прийти null (акт без строк) — раньше toFixed падал.
      valueFormatter: (params) => formatActAmount(params.value)
    },
    {
      field: 'createDt',
      headerName: t('acts:signedActs.createDate'),
      minWidth: 170,
      valueFormatter: (params) => new Date(params.value).toLocaleString('ru-RU')
    }
  ];

  return (
    <Box sx={{ mt: 2, mb: 2 }}>
      <Card>
        <CardHeader
          subheader={
            <Box sx={{ mb: { md: 2 } }}>
              <Typography variant="h5" color="textPrimary">
                <span>{t('acts:signedActs.title')}</span>
              </Typography>
            </Box>
          }
        />
        <DataGridPro
          columns={columns}
          rows={acts}
          pageSize={pageSize}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          rowsPerPageOptions={[5, 10, 20]}
          pagination
          localeText={getGridLocale()}
          componentsProps={{
            ...getGridPaginationLocaleProps()
          }}
          // OSI-808: сортировка по умолчанию — по периоду от нового к старому;
          // initialState (а не controlled sortModel), чтобы клик по заголовку продолжал работать
          initialState={{ sorting: { sortModel: [{ field: 'actPeriod', sort: 'desc' }] } }}
        />
      </Card>
      <SignedActsDialog viewModel={viewModel} />
    </Box>
  );
});
